import type { AgentScreen, ApprovalStatus, Attachment, ChatEntry, ChatResponse, ContextUsage, HandoffChatEntry, ServerEvent, ToolResult, Usage } from "@ark-bot/protocol";

import { text } from "./i18n.js";
import { ACTIVITY } from "./text/activity.js";

/** A subagent session shown nested under the `task` call that spawned it. */
export interface ChildSessionView {
  readonly sessionId: string;
  readonly subagent: string;
  readonly title?: string;
  readonly parts: readonly AssistantPart[];
  readonly finished: boolean;
  readonly status?: "completed" | "error" | "interrupted";
  readonly summary?: string;
}

/** One tool call as the outline shows it: args at start, live output, then the result. */
export interface ToolCallView {
  readonly kind: "tool";
  readonly callId: string;
  readonly name: string;
  readonly args: unknown;
  readonly progress: string;
  readonly result?: ToolResult;
  readonly child?: ChildSessionView;
}

export type AssistantPart =
  | { readonly kind: "text"; readonly text: string }
  | { readonly kind: "reasoning"; readonly text: string }
  | ToolCallView;

/** The outline ("Full conversation"): what the model was given and did, turn by turn. */
export type ChatItem =
  | { readonly kind: "user"; readonly turnId: string; readonly text: string; readonly attachments: readonly Attachment[]; readonly hidden?: boolean }
  | { readonly kind: "assistant"; readonly turnId: string; readonly parts: readonly AssistantPart[]; readonly finished: boolean; readonly usage?: Usage }
  | { readonly kind: "approval"; readonly approvalId: string; readonly tool: string; readonly summary: string; readonly reason: string; readonly status: ApprovalStatus }
  | { readonly kind: "condensed"; readonly sessionId: string; readonly condensedEntries: number; readonly epoch: number }
  | { readonly kind: "error"; readonly message: string; readonly retryable: boolean };

/** A tool call in flight, for the status line. */
export interface RunningCall {
  readonly name: string;
  readonly args: unknown;
  readonly sessionId: string;
}

export interface ChatState {
  /** The bubbles: exactly what the user has been shown. */
  readonly messages: readonly ChatEntry[];
  /** The outline items. */
  readonly items: readonly ChatItem[];
  readonly busy: boolean;
  /** Highest event seq applied; what a reconnect resumes from. */
  readonly lastSeq: number;
  /** Child session id -> the parent `task` call it renders under. */
  readonly children: Readonly<Record<string, string>>;
  /** Background subagent sessions still running; they keep going after the turn ends. */
  readonly childrenRunning: number;
  /** Tool calls in flight, by call id. */
  readonly running: Readonly<Record<string, RunningCall>>;
  /** Where the agent last pointed on the desktop, for the Computer pane's overlay. */
  readonly cursor: AgentCursor | null;
  /** In a group chat, the member taking its turn right now; null anywhere else. */
  readonly speaker: { readonly id: string; readonly name: string } | null;
  /** Bumped whenever a routine is written or run, so the panel refetches rather than guessing. */
  readonly routinesRevision: number;
  /** The same, for skills: the agent writes its own, so the panel cannot assume it is the only author. */
  readonly skillsRevision: number;
  readonly todosRevision: number;
  readonly memoryRevision: number;
  /** The same, for the agent's WhatsApp link and the threads of the people who write to it (§6.19). */
  readonly channelsRevision: number;
  /** The box's screens this agent has, in display order; its pane shows these and no others (§6.15). */
  readonly screens: readonly AgentScreen[];
  /** Older bubbles than the ones here exist on the host; the chat is read a page at a time from the bottom. */
  readonly hasMore: boolean;
  /** How full the agent's context is after its last call; null for a group, or before the page said. */
  readonly context: ContextUsage | null;
  /**
   * The last message the box took back because the account could not pay for
   * the model: why, what it said (so the composer can offer it again), and
   * when. Null until one is; only one whose bubble this page was showing.
   */
  readonly withdrawn: WithdrawnPrompt | null;
}

export interface WithdrawnPrompt {
  readonly message: string;
  readonly texts: readonly string[];
  readonly at: number;
  /** The event's seq, so the page tells each one once. */
  readonly seq: number;
}

/** The agent's last pointer action on the desktop: a point and what it did there, numbered so the overlay can animate each one. */
export interface AgentCursor {
  readonly x: number;
  readonly y: number;
  readonly type: "click" | "move" | "drag" | "scroll";
  readonly seq: number;
}

export const emptyChat: ChatState = { messages: [], items: [], busy: false, lastSeq: 0, children: {}, childrenRunning: 0, running: {}, cursor: null, speaker: null, routinesRevision: 0, skillsRevision: 0, todosRevision: 0, memoryRevision: 0, channelsRevision: 0, screens: [], hasMore: false, context: null, withdrawn: null };

/**
 * The chat as the host answered it (`GET /agents/:id/chat?limit=`): the
 * newest page of bubbles and the agent's state at that moment. The event
 * stream is opened from `eventSeq`, so everything after this arrives as
 * events and nothing before it is replayed (ARCHITECTURE §5.5).
 */
export function seedChat(page: ChatResponse): ChatState {
  return { ...emptyChat, messages: [...page.entries], busy: page.busy, lastSeq: page.eventSeq, screens: page.screens ?? [], hasMore: page.hasMore === true, context: page.context ?? null };
}

/** An older page, read when the user scrolls to the top; entries already shown are not repeated. */
export function prependMessages(state: ChatState, page: ChatResponse): ChatState {
  const known = new Set(state.messages.map((entry) => entry.id));
  const older = page.entries.filter((entry) => !known.has(entry.id));
  return { ...state, messages: [...older, ...state.messages], hasMore: page.hasMore === true };
}

/** The oldest bubble on screen, for the next page above it. */
export function oldestSeq(state: ChatState): number | null {
  return state.messages[0]?.seq ?? null;
}

const numberOr = (value: unknown, fallback: number | null): number | null => (typeof value === "number" && Number.isFinite(value) ? value : fallback);

/** The cursor after one tool call starts: pointer actions move it, everything else leaves it. */
export function cursorAfter(previous: AgentCursor | null, name: string, args: unknown): AgentCursor | null {
  if (!name.startsWith("computer_")) return previous;
  const record = typeof args === "object" && args != null ? (args as Record<string, unknown>) : {};
  const seq = (previous?.seq ?? 0) + 1;
  switch (name) {
    case "computer_click":
    case "computer_move": {
      const x = numberOr(record.x, null);
      const y = numberOr(record.y, null);
      return x == null || y == null ? previous : { x, y, type: name === "computer_click" ? "click" : "move", seq };
    }
    case "computer_drag": {
      const path = Array.isArray(record.path) ? record.path : [];
      const last = typeof path.at(-1) === "object" && path.at(-1) != null ? (path.at(-1) as Record<string, unknown>) : {};
      const x = numberOr(last.x, null);
      const y = numberOr(last.y, null);
      return x == null || y == null ? previous : { x, y, type: "drag", seq };
    }
    case "computer_scroll": {
      const x = numberOr(record.x, previous?.x ?? null);
      const y = numberOr(record.y, previous?.y ?? null);
      return x == null || y == null ? previous : { x, y, type: "scroll", seq };
    }
    default:
      return previous;
  }
}

/**
 * The hand-off the user has not answered: the agent asked for the box and is
 * parked until they press a button. The newest card decides, as it does in the
 * hub, so an answered one never keeps the banner up.
 */
export function pendingHandoff(messages: readonly ChatEntry[]): HandoffChatEntry | null {
  for (let index = messages.length - 1; index >= 0; index -= 1) {
    const entry = messages[index]!;
    if (entry.kind === "handoff") return entry.status === "waiting" ? entry : null;
  }
  return null;
}

export const SEND_TOOL = "send_message";
const DELIVERY_TOOLS = new Set([SEND_TOOL, "react_to_message"]);

function updateLast<T extends ChatItem>(items: readonly ChatItem[], kind: T["kind"], update: (item: T) => T): ChatItem[] {
  for (let index = items.length - 1; index >= 0; index -= 1) {
    const item = items[index]!;
    if (item.kind === kind) return [...items.slice(0, index), update(item as T), ...items.slice(index + 1)];
  }
  return [...items];
}

type Assistant = Extract<ChatItem, { kind: "assistant" }>;

/** Ensures the current turn has an open assistant item, then applies `update` to it. */
function withAssistant(state: ChatState, update: (item: Assistant) => Assistant): ChatState {
  const last = state.items.at(-1);
  const hasOpen = state.items.some((item) => item.kind === "assistant" && !item.finished);
  const items = hasOpen ? state.items : [...state.items, { kind: "assistant" as const, turnId: last?.kind === "user" ? last.turnId : "", parts: [], finished: false }];
  return { ...state, items: updateLast<Assistant>(items, "assistant", update) };
}

function appendPart(parts: readonly AssistantPart[], kind: "text" | "reasoning", delta: string): AssistantPart[] {
  const last = parts.at(-1);
  if (last != null && last.kind === kind) return [...parts.slice(0, -1), { kind, text: last.text + delta }];
  return [...parts, { kind, text: delta }];
}

function updateTool(parts: readonly AssistantPart[], callId: string, update: (tool: ToolCallView) => ToolCallView): AssistantPart[] {
  return parts.map((part) => (part.kind === "tool" && part.callId === callId ? update(part) : part));
}

/** Applies `update` to the tool call `callId` wherever it is: a background child keeps reporting after its parent's turn has ended. */
function withTool(state: ChatState, callId: string, update: (tool: ToolCallView) => ToolCallView): ChatState {
  for (let index = state.items.length - 1; index >= 0; index -= 1) {
    const item = state.items[index]!;
    if (item.kind !== "assistant" || !item.parts.some((part) => part.kind === "tool" && part.callId === callId)) continue;
    return { ...state, items: [...state.items.slice(0, index), { ...item, parts: updateTool(item.parts, callId, update) }, ...state.items.slice(index + 1)] };
  }
  return state;
}

/** Applies `update` to the child session nested under `callId`. */
function withChild(state: ChatState, callId: string, update: (child: ChildSessionView) => ChildSessionView): ChatState {
  return withTool(state, callId, (tool) => (tool.child == null ? tool : { ...tool, child: update(tool.child) }));
}

/** Folds a part-level event into a list of parts; shared by the main turn and nested sessions. */
function applyToParts(parts: readonly AssistantPart[], event: ServerEvent): readonly AssistantPart[] {
  switch (event.type) {
    case "text.delta":
      return appendPart(parts, "text", event.text);
    case "reasoning.delta":
      return appendPart(parts, "reasoning", event.text);
    case "tool.started":
      return [...parts, { kind: "tool", callId: event.callId, name: event.name, args: event.args, progress: "" }];
    case "tool.progress":
      return updateTool(parts, event.callId, (tool) => ({ ...tool, progress: tool.progress + event.chunk }));
    case "tool.finished":
      return updateTool(parts, event.callId, (tool) => ({ ...tool, result: event.result }));
    default:
      return parts;
  }
}

function withoutCalls(running: ChatState["running"], keep: (call: RunningCall, callId: string) => boolean): ChatState["running"] {
  const next: Record<string, RunningCall> = {};
  for (const [callId, call] of Object.entries(running)) if (keep(call, callId)) next[callId] = call;
  return next;
}

/**
 * Folds one server event into the chat view. Pure, so the same function
 * renders a replayed history and a live stream identically (ARCHITECTURE §5.6).
 */
export function applyEvent(state: ChatState, event: ServerEvent, seq: number): ChatState {
  // A reconnect can replay an event already applied; the log is append-only,
  // so anything at or below the cursor has been seen.
  if (seq <= state.lastSeq) return state;
  const next = { ...state, lastSeq: seq };
  const parentCall = "sessionId" in event && event.sessionId != null ? state.children[event.sessionId] : undefined;
  switch (event.type) {
    case "chat.entry":
      return next.messages.some((entry) => entry.id === event.entry.id) ? next : { ...next, messages: [...next.messages, event.entry] };
    case "chat.entry.updated":
      return { ...next, messages: next.messages.map((entry) => (entry.id === event.entry.id ? event.entry : entry)) };
    case "prompt.withdrawn": {
      // The account could not pay for it: its bubbles and its turn leave the page, as they left the box.
      const gone = new Set(event.chatIds);
      const texts = next.messages.flatMap((entry) => (gone.has(entry.id) && entry.kind === "user" ? [entry.text] : []));
      return {
        ...next,
        messages: next.messages.filter((entry) => !gone.has(entry.id)),
        items: next.items.filter((item) => !((item.kind === "user" || item.kind === "assistant") && item.turnId === event.turnId)),
        withdrawn: texts.length === 0 ? next.withdrawn : { message: event.message, texts, at: event.at, seq },
      };
    }
    case "turn.started":
      return { ...next, busy: true };
    case "user.message":
      // A prompt reached the model: the reply so far is closed and a fresh one opens after it.
      return {
        ...next,
        items: [
          ...next.items.map((item) => (item.kind === "assistant" && !item.finished ? { ...item, finished: true } : item)),
          { kind: "user", turnId: event.turnId, text: event.text, attachments: event.attachments ?? [], ...(event.hidden === true ? { hidden: true } : {}) },
          { kind: "assistant", turnId: event.turnId, parts: [], finished: false },
        ],
      };
    case "tool.started": {
      const tracked = { ...next, running: { ...next.running, [event.callId]: { name: event.name, args: event.args, sessionId: event.sessionId } }, cursor: cursorAfter(next.cursor, event.name, event.args) };
      if (parentCall != null) return withChild(tracked, parentCall, (child) => ({ ...child, parts: applyToParts(child.parts, event) }));
      return withAssistant(tracked, (item) => ({ ...item, parts: applyToParts(item.parts, event) }));
    }
    case "tool.finished": {
      const tracked = { ...next, running: withoutCalls(next.running, (_call, callId) => callId !== event.callId) };
      if (parentCall != null) return withChild(tracked, parentCall, (child) => ({ ...child, parts: applyToParts(child.parts, event) }));
      return withAssistant(tracked, (item) => ({ ...item, parts: applyToParts(item.parts, event) }));
    }
    case "text.delta":
    case "reasoning.delta":
    case "tool.progress":
      if (parentCall != null) return withChild(next, parentCall, (child) => ({ ...child, parts: applyToParts(child.parts, event) }));
      return withAssistant(next, (item) => ({ ...item, parts: applyToParts(item.parts, event) }));
    case "session.spawned": {
      if (event.callId == null) return next;
      const child: ChildSessionView = { sessionId: event.sessionId, subagent: event.subagent, ...(event.title == null ? {} : { title: event.title }), parts: [], finished: false };
      const attached = withTool(next, event.callId, (tool) => ({ ...tool, child }));
      return { ...attached, children: { ...attached.children, [event.sessionId]: event.callId }, childrenRunning: attached.childrenRunning + 1 };
    }
    case "session.finished": {
      const counted = { ...next, childrenRunning: Math.max(0, next.childrenRunning - 1), running: withoutCalls(next.running, (call) => call.sessionId !== event.sessionId) };
      if (parentCall == null) return counted;
      return withChild(counted, parentCall, (child) => ({ ...child, finished: true, summary: event.summary, ...(event.status == null ? {} : { status: event.status }) }));
    }
    case "room.speaker":
      return { ...next, speaker: event.member };
    case "routines.changed":
      return { ...next, routinesRevision: next.routinesRevision + 1 };
    case "skills.changed":
      return { ...next, skillsRevision: next.skillsRevision + 1 };
    case "todos.changed":
      return { ...next, todosRevision: next.todosRevision + 1 };
    case "memory.changed":
      return { ...next, memoryRevision: next.memoryRevision + 1 };
    case "channel.changed":
    case "channel.thread":
      return { ...next, channelsRevision: next.channelsRevision + 1 };
    case "screens":
      // The host sends the whole list, so there is nothing here to reconcile:
      // a screen another agent has taken is simply gone from it (§6.15).
      return { ...next, screens: event.screens };
    case "turn.finished":
      return {
        ...next,
        busy: false,
        // The room turn is over, so nobody is speaking; a crash mid-member leaves this to clear it.
        speaker: null,
        // Calls of background children stay; the turn's own are over.
        running: withoutCalls(next.running, (call) => next.children[call.sessionId] != null),
        items: updateLast<Assistant>(next.items, "assistant", (item) => ({ ...item, finished: true, usage: event.usage })),
        // The gauge follows the last call; the window it is measured against is the page's.
        context: event.context == null || next.context == null ? next.context : { ...next.context, ...event.context },
      };
    case "error":
      if (parentCall != null) return withChild(next, parentCall, (child) => ({ ...child, parts: [...child.parts, { kind: "text", text: text(ACTIVITY).error(event.message) }] }));
      return { ...next, items: [...next.items, { kind: "error", message: event.message, retryable: event.retryable }] };
    case "approval.requested":
      // The card itself is a chat entry; the outline notes that the call is waiting on it.
      return { ...next, items: [...next.items, { kind: "approval", approvalId: event.approvalId, tool: event.tool, summary: event.summary, reason: event.reason, status: "pending" }] };
    case "approval.resolved":
      return { ...next, items: next.items.map((item) => (item.kind === "approval" && item.approvalId === event.approvalId ? { ...item, status: event.status } : item)) };
    case "context.condensed":
      // A child's condensation is its own business; the main session's shows in the outline.
      if (parentCall != null) return next;
      return {
        ...next,
        items: [...next.items, { kind: "condensed", sessionId: event.sessionId, condensedEntries: event.condensedEntries, epoch: event.epoch }],
        // The gauge drops with it, to the host's estimate until the next call reports the real size.
        context: event.context == null || next.context == null ? next.context : { ...next.context, ...event.context },
      };
    default:
      return next;
  }
}

export function applyEvents(state: ChatState, events: ReadonlyArray<{ seq: number; event: ServerEvent }>): ChatState {
  return events.reduce((current, { seq, event }) => applyEvent(current, event, seq), state);
}

/** What the agent is up to, for the status line under the chat. */
export interface AgentStatus {
  /** A send_message call is in flight: the typing indicator. */
  readonly composing: boolean;
  /** A short phrase for the tool being used, when it is not a send. */
  readonly activity: string | null;
  /** Anything is going on: a turn, or a background subagent. */
  readonly working: boolean;
  /** A card is waiting on the user; the agent's call is paused on it. */
  readonly awaitingApproval: boolean;
  /** In a group chat, whose turn it is; the status line names them instead of the room. */
  readonly speaker: string | null;
}

function hostOf(value: unknown): string | null {
  if (typeof value !== "string") return null;
  try {
    return new URL(value).hostname || null;
  } catch {
    return null;
  }
}

/** A few words for a tool call, as a person would describe what the agent is doing. */
export function describeActivity(name: string, args: unknown): string {
  const t = text(ACTIVITY);
  const record = typeof args === "object" && args != null ? (args as Record<string, unknown>) : {};
  if (name === SEND_TOOL) return t.typing;
  if (name === "react_to_message") return t.reacting;
  if (name === "task") return t.startingTask;
  if (name === "stop_subagent") return t.stoppingTask;
  if (name === "send_to_agent") return t.writingToBot;
  if (name === "check_subagent") return t.checkingProgress;
  if (name === "message_subagent") return t.updatingTask;
  if (name === "shell") return t.runningCommand;
  if (["read", "ls", "glob", "grep"].includes(name)) return t.lookingThroughFiles;
  if (["write", "edit"].includes(name)) return t.editingFiles;
  if (name === "web_search") return t.searchingWeb;
  if (name === "web_fetch") {
    const host = hostOf(record.url);
    return host == null ? t.readingPage : t.readingHost(host);
  }
  if (name.startsWith("browser_")) {
    const host = hostOf(record.url);
    return host == null ? t.browsing : t.browsingHost(host);
  }
  if (name.startsWith("computer_")) return t.usingDesktop;
  return t.usingTool(name);
}

/** The call the status line describes: the newest one in flight that is not a delivery. */
function currentWork(state: ChatState): RunningCall | undefined {
  return Object.values(state.running).reverse().find((call) => !DELIVERY_TOOLS.has(call.name));
}

/** Whether the agent is working the desktop right now, whatever language the status line says it in. */
export function usingDesktop(state: ChatState): boolean {
  return currentWork(state)?.name.startsWith("computer_") === true;
}

export function agentStatus(state: ChatState): AgentStatus {
  const calls = Object.values(state.running);
  const composing = calls.some((call) => call.name === SEND_TOOL);
  const work = currentWork(state);
  return {
    composing,
    activity: work == null ? null : describeActivity(work.name, work.args),
    working: state.busy || state.childrenRunning > 0,
    awaitingApproval: state.messages.some((entry) => entry.kind === "approval" && entry.status === "pending"),
    speaker: state.speaker?.name ?? null,
  };
}

/** A one-line description of a tool call for the outline's collapsed header. */
export function summarizeArgs(name: string, args: unknown): string {
  if (typeof args !== "object" || args == null) return name;
  const record = args as Record<string, unknown>;
  if (name === "task" && typeof record.subagent === "string") {
    const label = typeof record.title === "string" && record.title.length > 0 ? record.title : typeof record.prompt === "string" ? record.prompt : "";
    return `task ${record.subagent}${label.length > 0 ? `: ${label.length > 70 ? `${label.slice(0, 67)}…` : label}` : ""}`;
  }
  if (name === SEND_TOOL && typeof record.content === "string") return `${name} ${record.content.length > 80 ? `${record.content.slice(0, 77)}…` : record.content}`;
  if (name.startsWith("computer_") && typeof record.x === "number" && typeof record.y === "number") return `${name} (${record.x}, ${record.y})`;
  const key = ["command", "path", "pattern", "url", "ref", "key", "text", "app", "message", "subagent_id"].find((candidate) => typeof record[candidate] === "string");
  if (key == null) return name;
  const value = String(record[key]);
  return `${name} ${value.length > 80 ? `${value.slice(0, 77)}…` : value}`;
}
